import type { OfferPriceState } from "@/utils/offerPriceState";
import { ArrowDown } from "lucide-react";
import { AnimatedPrice } from "./AnimatedPrice";
import { PriceStatusSlot } from "./OfferPriceStatus";

export function PriceInfo({
  priceMinFormatted,
  priceMaxFormatted,
  priceSavingFormatted,
  enableAnimation = false,
  align = "start",
  className = "",
  priceState = "ready"
}: {
  priceMinFormatted: string;
  priceMaxFormatted?: string;
  priceSavingFormatted?: string;
  enableAnimation?: boolean;
  /** `end` right-aligns the block, to sit over a right-aligned price column. */
  align?: "start" | "end";
  className?: string;
  /**
   * Anything but `ready` replaces the whole range with a loader or an error:
   * the range spans offers whose price is not known yet (KAN-343).
   */
  priceState?: OfferPriceState;
}) {
  const isEnd = align === "end";
  if (priceState !== "ready") {
    return (
      <div
        className={`flex flex-col gap-1 ${isEnd ? "items-end" : "items-start"} ${className}`}
      >
        <span className="text-[10px] font-medium uppercase leading-none tracking-wide text-gray-500">
          From
        </span>
        <PriceStatusSlot state={priceState} />
      </div>
    );
  }
  if (!isEnd) {
    // Left-aligned (the `full` card's pane): a small `FROM` label over one
    // baseline row - the price large, then the struck-out was-price and the
    // savings pill trailing it - so the eye reads label, price, then saving.
    //
    // `text-xl` sets the price one step above the card's title (`xl:text-lg`)
    // and no further: it has to lead the pane without dwarfing the title beside
    // it or the supplier prices it is the minimum of. At `text-2xl` it read as
    // a different card's element. The was-price and pill then sit a step below
    // it again, so the three sizes descend in the order they should be read.
    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        <span className="text-[10px] font-medium uppercase leading-none tracking-wide text-gray-500">
          From
        </span>
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-xl font-bold leading-none text-purple-800">
            {enableAnimation ? (
              <AnimatedPrice
                value={priceMinFormatted}
                highlightOnChange={true}
              />
            ) : (
              priceMinFormatted
            )}
          </span>
          {priceMaxFormatted && (
            <span className="text-xs leading-none text-gray-400 line-through">
              {enableAnimation ? (
                <AnimatedPrice
                  value={priceMaxFormatted}
                  highlightOnChange={true}
                />
              ) : (
                priceMaxFormatted
              )}
            </span>
          )}
          {priceSavingFormatted && (
            <span className="inline-block h-5 whitespace-nowrap rounded-full border border-green-600 bg-transparent pl-1.5 pr-2 text-xs font-semibold leading-[18px] text-green-700">
              <ArrowDown
                aria-hidden="true"
                className="mr-0.5 inline h-3 w-3 align-[-1px]"
              />
              {priceSavingFormatted}
            </span>
          )}
        </div>
      </div>
    );
  }
  return (
    // `FROM` sits beside the price, on its line; above it, the was-price shares
    // the price's right edge. When right-aligned the savings figure hangs
    // beside the price, outside that edge (a zero-width, baseline-aligned
    // slot), so the two prices line up - the caller must leave room for it.
    // The savings are an outlined, transparent pill: a filled green pill would
    // read as the "In Stock" pill in the table below. Wraps rather than
    // overflows: nothing here can truncate; a half-shown price is worse than a
    // second line. See storefront#145 (P4).
    <div
      className={`flex flex-col gap-1 ${isEnd ? "items-end" : "items-start"} ${className}`}
    >
      {priceMaxFormatted && (
        <span className="text-xs leading-none text-gray-400 line-through">
          {enableAnimation ? (
            <AnimatedPrice value={priceMaxFormatted} highlightOnChange={true} />
          ) : (
            priceMaxFormatted
          )}
        </span>
      )}
      {/* FROM, price and savings share one baseline row. */}
      <div className="flex items-baseline">
        <span className="mr-1.5 text-[10px] font-medium uppercase leading-none tracking-wide text-gray-500">
          From
        </span>
        <span className="text-lg font-bold leading-none text-purple-800">
          {enableAnimation ? (
            <AnimatedPrice value={priceMinFormatted} highlightOnChange={true} />
          ) : (
            priceMinFormatted
          )}
        </span>
        {priceSavingFormatted && (
          <span className={`pl-1.5 ${isEnd ? "w-0 whitespace-nowrap" : ""}`}>
            <span className="inline-flex h-5 items-center gap-0.5 rounded-full border border-green-600 bg-transparent pl-1.5 pr-2 text-xs font-semibold leading-none text-green-700">
              <ArrowDown aria-hidden="true" className="h-3 w-3" />
              {priceSavingFormatted}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
